import React from 'react';
import useSocialProof from '../utils/useSocialProof';

// Real-time social proof strip under the landing hero. Every element has a
// minimum threshold and is hidden below it — with few users the bar renders
// nothing at all, and elements light up automatically as usage grows.
// Numbers are real (never fabricated); story titles are anonymized
// server-side so no real child names appear.
const THRESHOLDS = {
  storiesToday:   10,  // show "X stories in the last 24 hours"
  storiesTotal:   25,  // fallback: show all-time count instead
  lastStoryMins: 180,  // show "last story N minutes ago" only if recent
  tickerTitles:    5,  // min titles for the scrolling ticker
};

const timeLabel = (mins) => {
  if (mins < 2) return 'moments ago';
  if (mins < 60) return `${mins} minutes ago`;
  const h = Math.round(mins / 60);
  return h === 1 ? '1 hour ago' : `${h} hours ago`;
};

const LiveActivityBar = () => {
  const stats = useSocialProof();
  if (!stats) return null;

  const showToday = stats.storiesToday >= THRESHOLDS.storiesToday;
  const showTotal = !showToday && stats.storiesTotal >= THRESHOLDS.storiesTotal;
  const showLast = !!stats.lastStory && stats.lastStory.minutesAgo <= THRESHOLDS.lastStoryMins;
  const titles = stats.recentTitles || [];
  const showTicker = titles.length >= THRESHOLDS.tickerTitles;

  if (!showToday && !showTotal && !showLast && !showTicker) return null;

  return (
    <section className="py-3 bg-white border-bottom">
      <style>{`
        @keyframes lab-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes lab-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .lab-ticker:hover .lab-ticker-track { animation-play-state: paused; }
      `}</style>
      <div className="container">

        {(showToday || showTotal || showLast) && (
          <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 gap-md-4">
            {(showToday || showTotal) && (
              <span className="d-flex align-items-center gap-2" style={{color:'var(--text-main)', fontSize:'14px', fontWeight:600}}>
                <span style={{width:'8px', height:'8px', borderRadius:'50%', background:'#22c55e', animation:'lab-pulse 1.6s ease-in-out infinite', flexShrink:0}}></span>
                {showToday
                  ? <>{stats.storiesToday.toLocaleString()} stories created in the last 24 hours</>
                  : <>{stats.storiesTotal.toLocaleString()} personalised stories created so far</>}
              </span>
            )}
            {showLast && (
              <span style={{color:'var(--text-muted2)', fontSize:'14px'}}>
                🕘 Last story: {timeLabel(stats.lastStory.minutesAgo)}
                {stats.lastStory.age ? ` — for a ${stats.lastStory.age}-year-old` : ''}
              </span>
            )}
          </div>
        )}

        {showTicker && (
          <div className="lab-ticker mt-3" style={{overflow:'hidden'}}>
            <div className="lab-ticker-track d-flex gap-2" style={{width:'max-content', animation:`lab-scroll ${Math.max(20, titles.length * 5)}s linear infinite`}}>
              {[...titles, ...titles].map((t, i) => (
                <span key={i} className="badge rounded-pill" style={{background:'var(--item-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontSize:'12px', fontWeight:500, padding:'8px 14px'}}>
                  📖 {t}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default LiveActivityBar;
